import React, { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import { PasswordInput } from "./../../../Components/ui/input";
import OTPVerification from "./OtpValidate";
import { useSettings } from "./../../../hooks";
import ButtonLoader from "./../../../Components/Loader";

function ResetPassword({ AccountData }) {
  const {
    showOTP,
    errors,
    setErrors,
    passwordForm,
    setPasswordForm,
    forgotPasswordResetIsLoading,
    setShowOTP,
  } = useSettings();

  useEffect(() => {
    return () => (
      setShowOTP(false),
      setPasswordForm({ oldPassword: "", newPassword: "", confirmPassword: "" })
    );
  }, []);

  const [validations, setValidations] = useState({
    hasLength: false,
    hasCase: false,
    hasSpecial: false,
  });

  const validatePassword = (password) => {
    setValidations({
      hasLength: password.length >= 8,
      hasCase: /^(?=.*[a-z])(?=.*[A-Z])/.test(password),
      hasSpecial: /^(?=.*[0-9])(?=.*[!@#$%^&*])/.test(password),
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    if (name === "newPassword") {
      validatePassword(value);

      if (passwordForm.confirmPassword) {
        if (passwordForm.confirmPassword !== value) {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "Passwords do not match",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            confirmPassword: "",
          }));
        }
      }
    }

    if (name === "confirmPassword") {
      if (value !== passwordForm.newPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "Passwords do not match",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "",
        }));
      }
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!passwordForm.oldPassword) {
      newErrors.oldPassword = "Please enter your old password";
    }

    if (!passwordForm.newPassword) {
      newErrors.newPassword = "Please enter a new password";
    } else if (!Object.values(validations).every(Boolean)) {
      newErrors.newPassword = "Password does not meet all requirements";
    } else if (passwordForm.oldPassword === passwordForm.newPassword) {
      newErrors.newPassword = "New password must be different from old password";
    }

    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setShowOTP(true);
  };

  if (showOTP) {
    return (
      <OTPVerification
        // email={AccountData?.data?.email}
        ResetPassword={true}
        cssRequired={true}
        userName={AccountData?.data?.username}
      />
    );
  }
  return (
    <div className="mt-10 mb-10 overflow-scroll">
      <form
        className="flex flex-col md:flex-row gap-6 items-center"
        onSubmit={handlePasswordSubmit}
      >
        <div className=" order-2 md:order-1 space-y-6 w-full  2xl:w-1/3 ">
          <div>
            <label className="block font-geomanistRegular font-[400] text-[14px] leading-[20px] text-greyish mb-1">
              Enter old password
            </label>
            <PasswordInput
              name="oldPassword"
              value={passwordForm.oldPassword}
              onChange={handlePasswordChange}
              className={`w-full text-sm border font-geomanistBold ${
                errors.oldPassword && "border-red-500"
              }`}
              placeholder="Enter old Password"
            />
            {errors.oldPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.oldPassword}</p>
            )}
          </div>

          <div>
            <label className="block font-geomanistRegular font-[400] text-[14px] leading-[20px] text-greyish mb-1">
              Enter new password
            </label>
            <PasswordInput
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              className={`w-full text-sm border font-geomanistBold ${
                errors.newPassword && "border-red-500"
              }`}
              placeholder="Enter new password"
            />
            {errors.newPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
            )}
          </div>

          <div>
            <label className="block font-geomanistRegular font-[400] text-[14px] leading-[20px] text-greyish mb-1">
              Re-enter new password
            </label>
            <PasswordInput
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              className={`w-full text-sm border font-geomanistBold ${
                errors.confirmPassword && "border-red-500"
              }`}
              placeholder="Re-enter new password"
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <div className="flex">
            <button
              type="submit"
              className="bg-blue-600 flex text-white px-4 py-2 rounded font-bold text-sm"
            >
              {forgotPasswordResetIsLoading && (
                <ButtonLoader className={"mr-2 h-"} />
              )}
              Change Password
            </button>
          </div>
        </div>

        <div className="order-1 md:order-2  w-full  space-y-2">
          <div className="flex items-center gap-2">
            {validations.hasLength ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <X className="w-4 h-4 text-gray-300" />
            )}
            <span
              className={`font-normal text-sm ${
                validations.hasLength ? "text-green-500" : "text-gray-300"
              }`}
            >
              Use 8 Or More Characters
            </span>
          </div>
          <div className="flex items-center gap-2">
            {validations.hasCase ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <X className="w-4 h-4 text-gray-300" />
            )}
            <span
              className={`font-normal text-sm ${
                validations.hasCase ? "text-green-500" : "text-gray-300"
              }`}
            >
              Use Upper And Lowercase Alphabets (E.g Aa)
            </span>
          </div>
          <div className="flex items-center gap-2">
            {validations.hasSpecial ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <X className="w-4 h-4 text-gray-300" />
            )}
            <span
              className={`font-normal text-sm ${
                validations.hasSpecial ? "text-green-500" : "text-gray-300"
              }`}
            >
              Use At least One Number And One Special Character
            </span>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ResetPassword;
